import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Toast from '../components/Toast';
import CowAvatar from '../components/CowAvatar';

export default function CreateVaquita() {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [step, setStep] = useState(1);
  const [createdVaquita, setCreatedVaquita] = useState(null);
  const [friends, setFriends] = useState([]);
  const [invited, setInvited] = useState({});
  const navigate = useNavigate();

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });
  const clearToast = useCallback(() => setToast(null), []);

  const handleCreate = async () => {
    if (!formData.name.trim()) { showToast('El nombre no puede estar vacío', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.post('/vaquita/create', formData);
      setCreatedVaquita(res.data);
      const fr = await api.get('/friends');
      setFriends(fr.data);
      setStep(2);
    } catch (err) {
      showToast(err.response?.data?.message || 'Error al crear la vaquita', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (friendUserId) => {
    try {
      await api.post(`/vaquita-invitations/invite?vaquitaId=${createdVaquita.id}&userId=${friendUserId}`);
      setInvited(prev => ({ ...prev, [friendUserId]: true }));
    } catch {}
  };

  const styles = `
    .create-vaquita-container { padding: 2rem; max-width: 600px; margin: 0 auto; }
    .create-vaquita-card { background: var(--bg-secondary); border-radius: 12px; padding: 2.5rem; box-shadow: 0 4px 12px var(--shadow-color); }
    .title { color: var(--text-primary); font-size: 2rem; margin-bottom: 2rem; text-align: center; }
    .form-group { margin-bottom: 1.5rem; }
    label { display: block; color: var(--text-primary); margin-bottom: 0.5rem; font-size: 1rem; }
    input, textarea { width: 100%; padding: 0.8rem 1.2rem; border: 2px solid var(--border-color); border-radius: 8px; font-size: 1rem; background: var(--bg-primary); color: var(--text-primary); transition: all 0.2s ease; box-sizing: border-box; }
    input:focus, textarea:focus { outline: none; border-color: var(--accent-color); box-shadow: 0 0 0 3px var(--accent-color-light); }
    textarea { resize: vertical; min-height: 100px; }
    .create-button { width: 100%; padding: 1rem 2rem; border: none; border-radius: 8px; font-size: 1.1rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; background: var(--accent-color); color: white; box-shadow: 0 2px 4px var(--shadow-color); }
    .create-button:hover:not(:disabled) { background: var(--accent-hover); transform: translateY(-2px); }
    .create-button:disabled { opacity: 0.7; cursor: not-allowed; }
    .step-badge { display: inline-block; background: #d1fae5; color: #065f46; font-size: 0.8rem; font-weight: 700; padding: 0.3rem 0.75rem; border-radius: 20px; margin-bottom: 0.75rem; }
    .step-hint { color: var(--text-muted); font-size: 0.9rem; margin: -1rem 0 1.5rem; text-align: center; }
    .friends-invite-list { display: flex; flex-direction: column; gap: 0.6rem; margin-bottom: 0.5rem; }
    .friend-invite-row { display: flex; align-items: center; gap: 0.75rem; background: var(--bg-primary); border-radius: 10px; padding: 0.6rem 0.9rem; }
    .friend-invite-name { flex: 1; color: var(--text-primary); font-weight: 500; font-size: 0.95rem; }
    .invite-btn { background: var(--accent-color); color: white; border: none; padding: 0.35rem 0.9rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; }
    .invited-badge { font-size: 0.82rem; color: #065f46; font-weight: 600; }
    .no-friends-msg { color: var(--text-muted); text-align: center; padding: 1.5rem 0; font-size: 0.9rem; }
  `;

  if (step === 2) {
    return (
      <div className="create-vaquita-container">
        {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}
        <div className="create-vaquita-card">
          <div style={{textAlign:'center'}}>
            <span className="step-badge">✓ Vaquita creada</span>
            <h2 className="title" style={{marginBottom:'0.5rem'}}>Invitar amigos 👥</h2>
            <p className="step-hint">¿Quiénes participarán en <strong>{createdVaquita.name}</strong>?</p>
          </div>

          {friends.length === 0 ? (
            <p className="no-friends-msg">No tienes amigos agregados aún. ¡Agrégalos desde la sección Amigos!</p>
          ) : (
            <div className="friends-invite-list">
              {friends.map(f => (
                <div key={f.userId} className="friend-invite-row">
                  <CowAvatar seed={f.profilePicture || f.userId} size={36} />
                  <span className="friend-invite-name">{f.name}</span>
                  {invited[f.userId]
                    ? <span className="invited-badge">Invitado ✓</span>
                    : <button className="invite-btn" onClick={() => handleInvite(f.userId)}>Invitar</button>
                  }
                </div>
              ))}
            </div>
          )}

          <button className="create-button" onClick={() => navigate(`/vaquita/code/${createdVaquita.inviteCode}`)} style={{marginTop:'1.5rem'}}>
            Ir a la vaquita →
          </button>
        </div>
        <style jsx>{styles}</style>
      </div>
    );
  }

  return (
    <div className="create-vaquita-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}
      <div className="create-vaquita-card">
        <h2 className="title">Crear nueva Vaquita 🐮</h2>

        <div className="form-group">
          <label htmlFor="name">Nombre de la vaquita</label>
          <input
            id="name" name="name"
            placeholder="Ej: Asado del viernes"
            value={formData.name}
            onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Descripción (opcional)</label>
          <textarea
            id="description" name="description"
            placeholder="Ej: Para juntar dinero para el asado del viernes en casa de Juan"
            value={formData.description}
            onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
            disabled={loading}
            rows={4}
          />
        </div>

        <button onClick={handleCreate} disabled={loading} className="create-button">
          {loading ? 'Creando...' : 'Crear Vaquita 🐮'}
        </button>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
}
